import React, { useState, useRef } from 'react';
import { FaTrashAlt, FaEdit, FaCloudUploadAlt, FaImage, FaStar } from 'react-icons/fa';
import { Modal, Form, Button, ProgressBar, CloseButton } from 'react-bootstrap';
import { getFirestore, doc, updateDoc, addDoc, collection, deleteDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { toast } from 'react-toastify';
import { spinner } from "../utils/Helper";

export default function XMenuList(props) {
    const { theme = 'dark', mode = "list" } = props;
    const isDark = theme === 'dark';
    const db = getFirestore();
    const storage = getStorage();
    const fileInputRef = useRef(null);

    const [showForm, setShowForm] = useState(props.show || false);
    const [docId, setDocId] = useState(props.id || null);
    const [progress, setProgress] = useState(0);
    const [formData, setFormData] = useState({
        name: props.name || '',
        details: props.details || '',
        cost: props.cost || '',
        isPrivate: props.isPrivate === undefined ? false : props.isPrivate,
        isASpecial: props.isASpecial || false,
        fileURLs: props.fileURLs || []
    });

    const handleClose = () => {
        setShowForm(false);
        if (props.onHide) props.onHide();
    };

    const handleSaveInfo = async () => {
        if (!formData.name.trim()) return toast.error("Title required");
        spinner(true);
        try {
            const cleanData = {
                name: formData.name,
                details: formData.details,
                cost: formData.cost,
                isPrivate: formData.isPrivate,
                isASpecial: formData.isASpecial,
                updated: new Date()
            };

            if (docId) {
                await updateDoc(doc(db, "specials", docId), cleanData);
                toast.success("Changes Saved");
            } else {
                const docRef = await addDoc(collection(db, "specials"), {
                    ...cleanData,
                    fileURLs: [],
                    created: new Date()
                });
                setDocId(docRef.id);
                toast.success("Item Created! You can now add a photo.");
            }
            if (props.onDeleteSuccess) props.onDeleteSuccess();
        } catch (err) {
            toast.error(err.message);
        } finally {
            spinner(false);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file || !docId) return;

        const uniqueID = Math.random().toString(36).substring(2, 9);
        const fileRef = ref(storage, `specials/${Date.now()}_${uniqueID}_${file.name}`);
        const uploadTask = uploadBytesResumable(fileRef, file);

        uploadTask.on('state_changed',
            (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
            null,
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                await updateDoc(doc(db, "specials", docId), { fileURLs: arrayUnion(url) });
                setFormData(prev => ({ ...prev, fileURLs: [...(prev.fileURLs || []), url] }));
                setProgress(0);
                toast.success("Photo Uploaded");
                if (props.onDeleteSuccess) props.onDeleteSuccess();
            }
        );
    };

    const confirmDelete = async () => {
        if (window.confirm("Delete this menu item?")) {
            spinner(true);
            try {
                if (formData.fileURLs?.length) {
                    await Promise.all(formData.fileURLs.map(url => deleteObject(ref(storage, url)).catch(() => { })));
                }
                await deleteDoc(doc(db, "specials", props.id));
                props.onDeleteSuccess();
            } finally { spinner(false); }
        }
    };

    if (props.public) {
        if (props.isPrivate) return null;
        return (
            <div className="menu-card position-relative overflow-hidden border-0 animated fadeIn mx-n3">
                <div className="image-container" style={{ backgroundColor: '#000', width: '100%' }}>
                    {props.fileURLs?.[0] ? (
                        <img src={props.fileURLs[0]} loading="lazy" alt={props.name} className="w-100 d-block" style={{ objectFit: 'cover', height: '350px' }} />
                    ) : (
                        <div className="d-flex align-items-center justify-content-center text-muted opacity-25" style={{ height: '300px' }}>
                            <FaImage size={50} />
                        </div>
                    )}
                </div>
                {props.isASpecial && (
                    <div className="position-absolute translate-middle-y" style={{ top: '80px', left: '0', zIndex: 10 }}>
                        <div className="px-3 py-1 fw-bold text-white shadow" style={{ background: 'linear-gradient(90deg, #ff8c00 0%, #ffbb00 100%)', borderRadius: '0 20px 20px 0', fontSize: '0.75rem' }}>
                            <FaStar className="me-1 mb-1" /> SPECIAL
                        </div>
                    </div>
                )}
                <div className="position-absolute top-0 start-0 w-100 p-3 d-flex justify-content-between align-items-start" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.95) 0%, transparent 100%)' }}>
                    <h3 className="fw-bold text-white m-0" style={{ fontSize: '1.5rem' }}>{props.name}</h3>
                    <span className="badge glass fs-5 ">{props.cost}</span>
                </div>
                {props.details && (
                    <div className="position-absolute bottom-0 start-0 w-100 p-3" style={{ background: 'linear-gradient(to top, rgba(0,0,0,1) 20%, transparent 100%)' }}>
                        <p className="text-white small m-0 fw-normal">{props.details}</p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            {mode !== "create_only" && (
                <div className={`p-3 mb-3 border rounded-3 ${isDark ? 'doc-card' : 'bg-light shadow-sm'}`}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 className="m-0 fw-bold">{props.isASpecial && <FaStar className="text-warning me-1 mb-1" />}{props.name} <span className="text-success ms-1 small">{props.cost}</span></h6>
                            <small className="opacity-50">{props.isPrivate ? "🔒 Private" : "🟢 Public"}</small>
                        </div>
                        <div className="d-flex gap-3">

                            <button className="circle-btn centerCircle shadow-sm" onClick={() => setShowForm(true)}><FaEdit size={12} /></button>
                            <button className="circle-btn centerCircle btn-delete shadow-sm" onClick={confirmDelete}><FaTrashAlt size={12} /></button>

                        </div>
                    </div>
                </div>
            )}

            <Modal show={showForm || props.show} onHide={handleClose} centered backdrop="static">
                <Modal.Body className={isDark ? "bg-dark text-white p-4 shadow-lg" : "p-4 shadow-lg"}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="fw-bold m-0 text-white">{docId ? 'Update Item' : 'New Menu Item'}</h4>
                        <CloseButton variant={isDark ? "white" : undefined} onClick={handleClose} />
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-info small fw-bold">ITEM NAME</Form.Label>
                        <Form.Control className={isDark ? "bg-dark glass-input" : ""} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-info small fw-bold">PRICE</Form.Label>
                        <Form.Control className={isDark ? "bg-dark glass-input" : ""} value={formData.cost} onChange={e => setFormData({ ...formData, cost: e.target.value })} />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-info small fw-bold">DESCRIPTION</Form.Label>
                        <Form.Control as="textarea" rows={2} className={isDark ? "bg-dark glass-input" : ""} value={formData.details} onChange={e => setFormData({ ...formData, details: e.target.value })} />
                    </Form.Group>

                    <div className="d-flex flex-column gap-2 mb-4">
                        <Form.Check type="switch" label="Public" checked={!formData.isPrivate} onChange={e => setFormData({ ...formData, isPrivate: !e.target.checked })} />
                        <Form.Check type="switch" label="⭐ Featured Special" checked={formData.isASpecial} onChange={e => setFormData({ ...formData, isASpecial: e.target.checked })} />
                    </div>

                    {/* ATTACHMENT SECTION - Appears middle if docId exists */}
                    {docId && (
                        <div className="p-3 mb-4 rounded bg-black bg-opacity-25 border border-secondary border-dashed animated fadeIn">
                            <h6 className="text-info fw-bold mb-3 small">ATTACH PHOTO</h6>
                            {formData.fileURLs?.length > 0 && (
                                <div className="mb-2 p-2 bg-dark rounded border border-secondary d-flex justify-content-between align-items-center">
                                    <span className="small opacity-50 text-truncate">Active Image</span>
                                    <FaTrashAlt className="text-danger pointer" size={12} onClick={async () => {
                                        if (!window.confirm("Remove photo?")) return;
                                        const url = formData.fileURLs[0];
                                        await deleteObject(ref(storage, url));
                                        await updateDoc(doc(db, "specials", docId), { fileURLs: arrayRemove(url) });
                                        setFormData(prev => ({ ...prev, fileURLs: [] }));
                                    }} />
                                </div>
                            )}
                            {progress > 0 && <ProgressBar now={progress} variant="info" className="mb-2" style={{ height: '5px' }} />}
                            <input type="file" className="d-none" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" />
                            <Button variant="outline-info" size="sm" className="w-100" onClick={() => fileInputRef.current.click()}>
                                <FaCloudUploadAlt className="me-1" /> {formData.fileURLs?.length > 0 ? "Change Photo" : "Upload Photo"}
                            </Button>
                        </div>
                    )}

                    {/* THE SINGLE SAVE BUTTON AT THE VERY BOTTOM */}
                    <Button variant="success" className="w-100 fw-bold py-2 shadow-sm mt-2" onClick={handleSaveInfo}>
                        {docId ? "Save Changes" : "Create Item"}
                    </Button>
                </Modal.Body>
            </Modal>
        </>
    );
}